import React from "react"
import { Modal, Input, Form, Tabs, Checkbox, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileCode,
  faRobot,
  faChartSimple,
  faFileCsv,
  faDatabase,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ExportMapModal from "./ExportMap/ExportMapModal.component"

import { useExportDataModal } from "features/QueryConsole/api/useExportDataModal"
import { collapseRemaps, useDriftQuery } from "features/Provenance"
import { useProjectStore } from "stores/projectStore"
import JsonExportPanel from "./ExportDataModal/panels/JsonExportPanel.component"
import JsonlExportPanel from "./ExportDataModal/panels/JsonlExportPanel.component"
import SpssExportPanel from "./ExportDataModal/panels/SpssExportPanel.component"
import CsvExportPanel from "./ExportDataModal/panels/CsvExportPanel.component"
import DqdExportPanel from "./ExportDataModal/panels/DqdExportPanel.component"
import DummyNotice from "features/DummyData/components/DummyNotice.component"
import { useQuestionnaireProjectId } from "features/DummyData/api/useDummyData"

import { QueryInstance } from "types/engine.types"
import { EXPORT_VARIABLE_TYPES } from "types/engineExport.types"

interface ExportDataModalProps {
  queryInstance: QueryInstance | null
  /** Whether the dialog is open; it is opened from the console's Tools menu. */
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ExportDataModal: React.FC<ExportDataModalProps> = ({
  queryInstance,
  open,
  onOpenChange,
}) => {
  const hook = useExportDataModal(queryInstance, open, onOpenChange)
  const project = useProjectStore((s) => s.project)

  // Advisory only (never blocks the export): how the loaded data differs from the questionnaire
  // at the moment of export. Cached: imports and checkpoint saves invalidate it.
  // Feeds the advisory line only; the export record takes its drift from the server.
  const { data: drift } = useDriftQuery(project?.id ?? "", hook.isModalVisible)

  // The advisory line, or null when there is nothing worth flagging.
  const driftAdvisory = ((): string | null => {
    if (!drift) return null
    if (!drift.hasQnr)
      return "This project has no questionnaire, so the export can't be checked against one."
    if (!drift.hasData) return null
    if (drift.parseStatus !== "ok") return null
    // Counted as the drift banner lists them
    const n = collapseRemaps(drift.drift ?? []).length
    if (n === 0) return null
    return `Exporting with ${n} difference${n === 1 ? "" : "s"} between the questionnaire and the data.`
  })()

  // Says so when the export is made from dummy data. A project without a
  // questionnaire never has dummy data: nothing is read for it.
  const projectId = useProjectStore((s) => s.project.id)
  const dummyFor = useQuestionnaireProjectId(projectId || undefined)

  if (!queryInstance) return null

  // The panels only start an export: the hook reports and records it
  const done = (exportFn: () => Promise<boolean>) => async (): Promise<void> => {
    await exportFn()
  }

  const tabItems = [
    {
      key: "json",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFileCode} />
          JSON
        </span>
      ),
      children: (
        <JsonExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={done(hook.handleJSONExport)}
          isExporting={hook.isExportingJSON}
        />
      ),
    },
    {
      key: "jsonl",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faRobot} />
          JSONL
        </span>
      ),
      children: (
        <JsonlExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={done(hook.handleJSONLExport)}
          isExporting={hook.isExportingJSONL}
        />
      ),
    },
    {
      key: "spss",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartSimple} />
          SPSS
        </span>
      ),
      children: (
        <SpssExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExportSPS={done(hook.handleSPSSExport)}
          onExportSAV={done(hook.handleSAVExport)}
          isExportingSPS={hook.isExportingSPS}
          isExportingSAV={hook.isExportingSAV}
          form={hook.spssForm}
          onOpenExportMap={hook.handleOpenExportMap}
        />
      ),
    },
    {
      key: "csv",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFileCsv} />
          CSV
        </span>
      ),
      children: (
        <CsvExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={done(hook.handleCSVExport)}
          isExporting={hook.isExportingCsvDirect}
          form={hook.csvForm}
          onOpenExportMap={hook.handleOpenExportMap}
          exportScripts={hook.exportScripts}
        />
      ),
    },
    {
      key: "dqd",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faDatabase} />
          DQD
        </span>
      ),
      children: (
        <DqdExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={done(hook.handleDQDExport)}
          isExporting={hook.isExportingDQD}
        />
      ),
    },
  ]

  return (
    <>
      <Modal
        centered
        title="Export Data"
        open={hook.isModalVisible}
        closable={true}
        onCancel={() => hook.setIsModalVisible(false)}
        footer={null}
        width={780}
        destroyOnHidden
      >
        <DummyNotice projectId={dummyFor} className="mb-4" />
        <Form layout="vertical" form={hook.filenameForm}>
          <Form.Item
            label="Filename"
            name="filename"
            rules={[{ required: true, message: "Please enter a Filename" }]}
          >
            <Input
              value={hook.inputValues.filename}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                hook.handleInputChange("filename", e.target.value)
              }
              placeholder="Enter filename"
            />
          </Form.Item>
        </Form>

        {driftAdvisory && (
          <div
            role="status"
            className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 shrink-0"
            />
            <span>
              {driftAdvisory}{" "}
              <span className="text-amber-700/80">
                This doesn&apos;t stop the export.
              </span>
            </span>
          </div>
        )}

        <Tooltip title="Every export is recorded in the project history. Tick this to also save its export record (.json) next to the exported file: the version ID, the history entry and the parts it is made from, with how to check each one.">
          <Checkbox
            checked={hook.includeExportRecord}
            onChange={(e) => hook.setIncludeExportRecord(e.target.checked)}
            className="mb-2"
          >
            Include export record (.json)
          </Checkbox>
        </Tooltip>

        <Tabs
          tabPosition="left"
          items={tabItems}
          defaultActiveKey="json"
          className="export-data-tabs"
        />

        <div className="flex justify-end mt-5">
          <DataQButton
            type="secondary"
            onClick={() => hook.setIsModalVisible(false)}
          >
            Cancel
          </DataQButton>
        </div>
      </Modal>

      <ExportMapModal
        open={hook.isExportMapModalOpen}
        onClose={() => hook.setIsExportMapModalOpen(false)}
        queryInstance={queryInstance}
        onExport={hook.handleExportWithMap}
        varType={
          hook.exportMapFormat === "csv"
            ? (hook.inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
                ? EXPORT_VARIABLE_TYPES.NUMBERING
                : EXPORT_VARIABLE_TYPES.LABELS)
            : (hook.inputValues.variableType === EXPORT_VARIABLE_TYPES.NUMBERING
                ? EXPORT_VARIABLE_TYPES.NUMBERING
                : EXPORT_VARIABLE_TYPES.LABELS)
        }
      />
    </>
  )
}

export default ExportDataModal
